import React from "react";

import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";

import Columns from "../Columns";
import shapeDesktopBlueImage from "./shape-desktop-blau.svg";
import shapeDesktopRedImage from "./shape-desktop-rot.svg";
import shapeDesktopBlueImageMobile from "./shape-mobile-blau.svg";
import shapeDesktopRedImageMobile from "./shape-mobile-rot.svg";

const ProductSimCard = styled.div`
    font-family: var(--font-family-vito);
    color: ${({ theme }) => theme.colors.blue.toString()};
    background-size: contain;
    background-position-y: bottom;
    background-position-x: center;
    background-repeat: no-repeat;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    width: 100%;
    cursor: pointer;
    padding: 20px 5px;
    position: relative;
    height: 147px;

    /* disable the default click effect on mobile touch */
    -webkit-tap-highlight-color: transparent;
    user-select: none;
    -webkit-touch-callout: none;
    ${({ theme }) => theme.breakpoints.upTo.extraSmall.css`
        justify-content: center;
        width: 110px;
    `}

    ${({ theme }) => theme.breakpoints.from.small.css`
        max-width: 226px;
        min-height: 170px;
    `}

    ${({ theme }) => theme.breakpoints.from.medium.css`
        height: 180px;
    `}

     ${({ theme }) => theme.breakpoints.from.normal.css`
        max-width: 312px;
        height: 212px;
        padding: 40px;
    `}
`;

const ContentWrapper = styled.div`
    flex: 1;
    align-self: center;
    ${({ theme }) => theme.breakpoints.upTo.extraSmall.css`
        display: flex;
        flex-direction: column; 
        align-items: center;
        justify-items: center;
        margin-top: 20px;
        text-align: center;
    `}
    ${({ theme }) => theme.breakpoints.from.small.css`
        margin-top: 15px;
    `}
`;

const ProductName = styled.div`
    line-break: auto;
    display: flex;
    font-weight: 700;
    font-size: 0.8rem;
    line-height: 1rem;
    margin-bottom: 5px;
    ${({ theme }) => theme.breakpoints.from.small.css`
        font-size: 1.2rem;
        line-height: 1.8rem;
    `}

    ${({ theme }) => theme.breakpoints.from.medium.css`
        font-size: 1.25rem;
        line-height: 1.8rem;
    `}

    ${({ theme }) => theme.breakpoints.from.large.css`
        font-size: 1.5rem;
        line-height: 1.8rem;
    `}
`;

const ProductPriceLabel = styled.div`
    display: flex;
    margin-top: 10px;
    font-size: 1rem;
    line-height: 1rem;
    ${({ theme }) => theme.breakpoints.from.small.css`
        font-size: 1rem;
        line-height: 1.2rem;
    `}
`;

const ProductPrice = styled.div`
    display: flex;
    font-size: 1.1rem;
    font-weight: 700;

    ${({ theme }) => theme.breakpoints.from.small.css`
        font-size: 1.5rem;
    `}
    ${({ theme }) => theme.breakpoints.from.medium.css`
        font-size: 2rem;
    `}
`;

const ProductSubline = styled.div`
    display: flex;
    font-size: 0.65rem;
    line-height: 0.75rem;

    ${({ theme }) => theme.breakpoints.from.small.css`
        font-size: 0.8rem;
        line-height: 1rem;
    `}
`;

const columnsCss = css`
    display: flex;
    justify-content: center;
    margin-bottom: 0 !important;
`;

const columnCss = css`
    display: flex;
    justify-content: center;
`;

const CalculatorProducts = ({
    products,
    activeIndex,
    onIndexChange,
    showTotalPriceLabel = false,
    subline = undefined,
}) => {
    const theme = useTheme();

    if (!products || products[1] === "ErrorException") {
        return null;
    }

    const active = css`
        background-image: url("${shapeDesktopRedImageMobile}");

        ${theme.breakpoints.from.small
            .css`background-image: url("${shapeDesktopRedImage}");`}

        color: white;

        &::after {
            position: absolute;
            bottom: 8px;
            left: 50%;
            width: 0;
            height: 0;
            content: "";
            border-top: 20px solid ${theme.colors.red.toString()};
            border-right: 20px solid transparent;
            border-left: 20px solid transparent;
            transform: translate(-20px, 100%);
        }
    `;

    const inactive = css`
        background-image: url("${shapeDesktopBlueImageMobile}");

        ${theme.breakpoints.from.small.css`
        background-image: url("${shapeDesktopBlueImage}");
    `}
    `;

    return (
        <React.Fragment>
            <Columns
                sameHeight
                css={columnsCss}
                className="is-mobile is-variable is-1-mobile"
            >
                {products[1] !== "ErrorException" &&
                    products.map((product, index) => {
                        /**
                         * Electricity and Gas have
                         * "Grundversorger Tarif" that
                         * should not be displayed
                         */
                        let showProduct = true;
                        if (product.isElectricity) {
                            product.name = product.tarifName;
                            product.prices = [];
                            product.prices.priceGross =
                                product.gesamtpreisBrutto;
                            if (product.isGrundversorgungsTarif) {
                                showProduct = false;
                            }
                        }

                        if (showProduct) {
                            const isActiveElement =
                                index === parseInt(activeIndex);

                            return (
                                <Columns.Column
                                    mobile={"one-third"}
                                    desktop="one-third"
                                    key={`product-${index}`}
                                    css={columnCss}
                                >
                                    <ProductSimCard
                                        onClick={() => onIndexChange(index)}
                                        css={css`
                                            ${isActiveElement
                                                ? active
                                                : inactive}
                                        `}
                                    >
                                        <ContentWrapper>
                                            <ProductName>
                                                {product.name}
                                            </ProductName>
                                            {showTotalPriceLabel && (
                                                <ProductPriceLabel>
                                                    Gesamtpreis:
                                                </ProductPriceLabel>
                                            )}
                                            <ProductPrice>
                                                {product.prices.priceGross}
                                                &nbsp;&euro;
                                            </ProductPrice>
                                            {subline && (
                                                <ProductSubline>
                                                    {subline}
                                                </ProductSubline>
                                            )}
                                        </ContentWrapper>
                                    </ProductSimCard>
                                </Columns.Column>
                            );
                        }
                        return null;
                    })}
            </Columns>
        </React.Fragment>
    );
};

export default CalculatorProducts;
